import React, { useMemo } from 'react';
import { Alert } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

const DATE_FORMAT = 'MMMM D, YYYY';

const TooltipBullets = ({ t }) => (
    <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
        <li>{t('expectedPayDate:TOOLTIP.ITEM_DD')}</li>
        <li>{t('expectedPayDate:TOOLTIP.ITEM_SUBMIT')}</li>
        <li>{t('expectedPayDate:TOOLTIP.ITEM_FACILITY')}</li>
    </ul>
);

const ExpeditedTooltipContent = ({ t }) => (
    <div>
        <p style={{ margin: 0 }}>{t('expectedPayDate:TOOLTIP.EXPEDITED_INTRO')}</p>
        <TooltipBullets t={t} />
        <p style={{ margin: '4px 0 0' }}>{t('expectedPayDate:TOOLTIP.EXPEDITED_OUTRO')}</p>
    </div>
);

const StandardTooltipContent = ({ t }) => (
    <div>
        <p style={{ margin: 0 }}>{t('expectedPayDate:TOOLTIP.STANDARD_INTRO')}</p>
        <p style={{ margin: '4px 0 0' }}>{t('expectedPayDate:TOOLTIP.STANDARD_LIST_HEADER')}</p>
        <TooltipBullets t={t} />
    </div>
);

const ExpectedPayDateCard = ({ timesheet }) => {
    const { t } = useTranslation();

    const display = useMemo(() => {
        if (!timesheet) return null;

        const { earlyExpectedPaydate, expectedPayDate, payType, status } = timesheet;

        const formattedEarlyDate = earlyExpectedPaydate
            ? moment(earlyExpectedPaydate).format(DATE_FORMAT)
            : null;
        const formattedExpectedDate = expectedPayDate
            ? moment(expectedPayDate).format(DATE_FORMAT)
            : null;

        const isExpedited = payType?.toLowerCase() === 'expedited';
        const date =
            isExpedited && formattedEarlyDate
                ? formattedEarlyDate
                : formattedExpectedDate || formattedEarlyDate;
        if (!date) return null;

        if (status === 'Approved') {
            return { label: t('expectedPayDate:LABEL.ACTUAL', { date }), variant: 'actual' };
        }

        const variant =
            isExpedited && formattedEarlyDate && formattedExpectedDate ? 'expedited' : 'standard';
        return { label: t('expectedPayDate:LABEL.ANTICIPATED', { date }), variant };
    }, [timesheet, t]);

    if (!display) return null;

    if (display.variant === 'actual') {
        return <Alert severity="success">{display.label}</Alert>;
    }

    const tooltipTitle =
        display.variant === 'expedited' ? (
            <ExpeditedTooltipContent t={t} />
        ) : (
            <StandardTooltipContent t={t} />
        );

    return (
        <Tooltip title={tooltipTitle} arrow>
            <Alert severity="info">{display.label}</Alert>
        </Tooltip>
    );
};

export default ExpectedPayDateCard;
